import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useCartStore } from '@astro/store'
import { api } from '@astro/api'
import { useToast } from '../context/ToastContext.jsx'
import { useWishlist, ensureOk } from '../hooks/useWishlist.js'
import { currencyFormatter } from '../components/sections/helper.js'

const isInStock = (product) => product.stock == null || parseInt(product.stock) > 0

export default function Wishlist() {
  const navigate = useNavigate()
  const { showToast } = useToast()
  const queryClient = useQueryClient()
  const addToCartStore = useCartStore((s) => s.add)

  // Same ['wishlist'] query as the header badge and product-card hearts, so
  // every removal here is reflected there too.
  const { lines: items, isPending, removeLine } = useWishlist()

  // One in-flight action at a time per line (remove or move).
  const [busyId, setBusyId] = useState(null)

  const removeItem = async (item) => {
    setBusyId(item.id)
    try {
      await removeLine(item.id)
      showToast('Removed from wishlist')
    } catch {
      showToast('Could not remove this item — try again.')
    } finally {
      setBusyId(null)
    }
  }

  const moveToCart = async (item) => {
    const product = item.product ?? {}
    const productId = item.product_id ?? product.id
    const title = product.name || 'Item'
    if (!isInStock(product)) {
      showToast(`${title} is out of stock`)
      return
    }

    setBusyId(item.id)
    try {
      // Upstream reports stock/validation failures as 200 + status:false.
      ensureOk(await api.cart.addToCart({ product_id: productId, quantity: 1 }))
    } catch (err) {
      showToast(err?.message || 'Could not move this item to the basket — try again.')
      setBusyId(null)
      return
    }

    // The header badge and Cart page read the local cart store, not the
    // ['cart'] query — invalidating react-query alone never touches them,
    // so mirror the addition into the store the same way ProductCard does.
    addToCartStore({
      productId,
      title,
      price: {
        amount: Math.round(parseFloat(product.total_price ?? product.price ?? 0) * 100) || 0,
        currency: 'INR',
      },
      qty: 1,
    })
    queryClient.invalidateQueries({ queryKey: ['cart'] })

    try {
      await removeLine(item.id)
      showToast(`${title} moved to basket`)
    } catch {
      showToast(`${title} added to basket, but could not be removed from your wishlist.`)
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="flex flex-col min-h-screen bg-background font-normal uppercase select-none">
      <div className="container px-6 mx-auto pt-28 md:pt-32 pb-12">
        {/* Back button */}
        <div className="mb-10 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/products')}
            className="group flex items-center gap-2 text-primary/40 font-normal uppercase tracking-widest text-[10px] hover:text-primary transition-colors"
          >
            <svg className="w-4 h-4 transition-transform group-hover:-translate-x-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m15 18-6-6 6-6"></path></svg>
            BACK TO SHOP
          </button>
          <h1 className="font-heading text-primary text-lg tracking-widest">MY WISHLIST</h1>
        </div>

        {isPending ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="bg-white border border-gray-100 rounded-3xl overflow-hidden shadow-sm animate-pulse">
                <div className="aspect-square bg-gray-200" />
                <div className="p-5 space-y-3">
                  <div className="h-4 w-3/4 rounded bg-gray-200" />
                  <div className="h-3 w-1/3 rounded bg-gray-100" />
                  <div className="h-10 w-full rounded-xl bg-gray-100" />
                </div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-3xl border border-border/40 shadow-sm py-24 text-center">
            <svg className="w-12 h-12 mx-auto text-primary/20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7Z" /></svg>
            <p className="mt-5 text-sm text-primary/50 font-body normal-case">Your wishlist is empty.</p>
            <button
              type="button"
              onClick={() => navigate('/products')}
              className="mt-6 inline-flex items-center justify-center h-11 px-8 rounded-xl bg-secondary text-white hover:bg-primary transition-all uppercase tracking-widest text-[10px]"
            >
              Explore Divine Products
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
            {items.map((item) => {
              const product = item.product ?? {}
              const title = product.name || item.title || 'Product'
              const img = product.image_url || item.img || '/images/1.webp'
              const displayPrice = currencyFormatter(product.total_price ?? product.price)
              const inStock = isInStock(product)
              return (
                <div key={item.id} className="flex flex-col bg-white border border-gray-100 rounded-3xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300">
                  <div className="relative aspect-square overflow-hidden bg-muted/10 border-b border-gray-100">
                    <img src={img} alt={title} className="object-contain w-full h-full p-4" />
                    <button
                      onClick={() => removeItem(item)}
                      disabled={busyId === item.id}
                      className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 hover:bg-white text-danger/60 hover:text-danger shadow-md flex items-center justify-center transition-all active:scale-90 disabled:opacity-60"
                      aria-label="Remove"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" /><line x1="10" x2="10" y1="11" y2="17" /><line x1="14" x2="14" y1="11" y2="17" /></svg>
                    </button>
                  </div>
                  <div className="p-5 flex flex-col flex-1 gap-3">
                    <h4 className="text-sm font-heading text-primary line-clamp-2 uppercase leading-tight min-h-[2.5em]">{title}</h4>
                    {displayPrice && <p className="text-primary font-normal">{displayPrice}</p>}
                    <button
                      type="button"
                      onClick={() => moveToCart(item)}
                      disabled={busyId === item.id || !inStock}
                      className="mt-auto inline-flex items-center justify-center whitespace-nowrap active:scale-95 h-10 px-4 rounded-xl bg-secondary text-white hover:bg-primary transition-all uppercase tracking-widest text-[9px] disabled:opacity-60 disabled:pointer-events-none"
                    >
                      {!inStock ? 'Out of Stock' : busyId === item.id ? 'Moving…' : 'Add to Basket'}
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
